"use client";

import { Button } from "@/components/ui/Button";
import type { ProductDetails } from "@/lib/centra";
import { formatPrice } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { BackInStock } from "../BuyBox/BuyBox";
import { useBuyBox } from "../BuyBox/useBuyBox";
import { ProductDetailsPrice } from "../ProductDetailsPrice";
import styles from "./StickyPdpCta.module.css";

const BUY_BOX_ID = "pdp-buybox";
const ATC_ID = "pdp-atc";

const scrollToBuyBox = () => {
	document.getElementById(BUY_BOX_ID)?.scrollIntoView({
		behavior: "smooth",
		block: "center",
	});
};

export const StickyPdpCta = ({ product }: { product: ProductDetails }) => {
	// Second useBuyBox instance (BuyBox has the first); both read size from URL params so they stay in sync.
	const buyBox = useBuyBox(product.items);
	const { getButtonProps, getButtonLabel } = buyBox;
	const buttonProps = getButtonProps();
	const dataType = buttonProps["data-type"];

	const [hidden, setHidden] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		let intersectionObserver: IntersectionObserver | undefined;
		let mutationObserver: MutationObserver | undefined;

		const observe = (target: HTMLElement) => {
			// The sticky bar overlaps the bottom of the viewport, so the ATC counts as hidden under it.
			const barHeight = rootRef.current?.offsetHeight ?? 0;
			intersectionObserver = new IntersectionObserver(
				([entry]) => setHidden(entry?.isIntersecting ?? false),
				{ rootMargin: `0px 0px -${barHeight}px 0px` },
			);
			intersectionObserver.observe(target);
		};

		// BuyBox renders inside <Suspense>, so the ATC may not exist yet when this effect runs.
		const existing = document.getElementById(ATC_ID);
		if (existing) {
			observe(existing);
		} else {
			mutationObserver = new MutationObserver(() => {
				const target = document.getElementById(ATC_ID);
				if (!target) return;
				mutationObserver?.disconnect();
				observe(target);
			});
			mutationObserver.observe(document.body, { childList: true, subtree: true });
		}

		return () => {
			mutationObserver?.disconnect();
			intersectionObserver?.disconnect();
		};
	}, []);

	return (
		<div ref={rootRef} className={styles.root} data-hidden={hidden}>
			<div className={styles.info}>
				<p className={styles.title}>{product.name}</p>
				<div className={styles.price}>
					<ProductDetailsPrice
						price={formatPrice(product.price)}
						isDiscounted={product.isDiscounted}
						priceBeforeDiscount={formatPrice(product.priceBeforeDiscount)}
						product={product}
					/>
				</div>
			</div>

			{dataType === "out_of_stock" ? (
				<BackInStock {...buyBox} />
			) : dataType === "select_size" ? (
				/* select_size: beige prompt; overrides centra's disabled button so the CTA scrolls to the size selector instead */
				<Button size="full" color="beige" onClick={scrollToBuyBox}>
					{getButtonLabel()}
				</Button>
			) : (
				/* add_to_cart / preorder / coming_soon (no sizes, single size, or already selected): black */
				<Button {...buttonProps} size="full" color="black">
					{getButtonLabel()}
				</Button>
			)}
		</div>
	);
};

StickyPdpCta.displayName = "StickyPdpCta";
